import { NavLink, Outlet } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { LayoutDashboard, FileText, PenLine, Building2, Globe, LogOut, ChevronLeft, Menu, Users, Image, Rocket, MapPin, Briefcase,Cpu } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useState, useEffect } from 'react';
import api from '@/lib/api';

const navItems = [
  { to: '/dashboard',     icon: LayoutDashboard, label: 'Dashboard' },
  { to: '/stories',       icon: FileText,        label: 'Newsroom', end: true },
  { to: '/stories/new',   icon: PenLine,         label: 'New Story' },
  { to: '/clients',       icon: Building2,       label: 'Clients' },
  { to: '/publications',  icon: Globe,           label: 'Publications' },
  { to: '/site-launcher', icon: Rocket,          label: 'Site Launcher',  roles: ['admin', 'editor'] },
  { to: '/local-areas',   icon: MapPin,          label: 'Local Areas',    roles: ['admin', 'editor'] },
  { to: '/ai-inference',  icon: Cpu,             label: 'AI Inference',   roles: ['admin'] },
  { to: '/users',         icon: Users,           label: 'User Management', roles: ['admin'], badge: true },
  { to: '/media',         icon: Image,           label: 'Media Library' },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const [collapsed, setCollapsed] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);

  useEffect(() => {
    if (user?.role !== 'admin') return;

    const fetchCount = async () => {
      try {
        const res = await api.get('/users/pending/count');
        setPendingCount(res.data.count || 0);
      } catch {
        // silently fail
      }
    };

    fetchCount();
    const interval = setInterval(fetchCount, 60000);
    return () => clearInterval(interval);
  }, [user?.role]);

  const roleColors = {
    admin:  'bg-blue-100 text-blue-700 border-blue-200',
    editor: 'bg-amber-100 text-amber-700 border-amber-200',
    caster: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  };

  return (
    <div className="min-h-screen bg-[#FFF8F0] flex" data-testid="app-layout">
      {/* Sidebar */}
      <aside
        data-testid="sidebar"
        className={`fixed top-0 left-0 h-screen bg-[#FFF4E8] border-r border-[#E7D2B8] flex flex-col z-40 transition-all duration-300 ${collapsed ? 'w-16' : 'w-64'}`}
      >
        {/* Logo */}
        <div className="flex items-center gap-3 px-4 h-16 border-b border-[#E7D2B8] shrink-0">
          <div className="w-14 h-14 shrink-0 flex items-center justify-center">
            <img src="/tempust-logo.png" alt="Tempust" className="w-14 h-14 object-contain" />
          </div>
          {!collapsed && <span className="text-lg font-bold text-[#1F1A17] font-outfit tracking-tight">Tempust</span>}
          <button onClick={() => setCollapsed(!collapsed)} className="ml-auto text-[#6E6258] hover:text-[#1F1A17] transition-colors">
            {collapsed ? <Menu size={18} /> : <ChevronLeft size={18} />}
          </button>
        </div>

        {/* Nav */}
        <nav className="flex-1 py-4 px-2 space-y-1 overflow-y-auto">
          {navItems
            .filter(item => !item.roles || item.roles.includes(user?.role))
            .map(({ to, icon: Icon, label, badge, end }) => {
              const showBadge = badge && pendingCount > 0;
              return (
                <NavLink
                  key={to}
                  to={to}
                  end={end}
                  data-testid={`nav-${label.toLowerCase().replace(/\s/g, '-')}`}
                  className={({ isActive }) =>
                    `relative flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all ${
                      isActive
                        ? 'bg-[#FFE4C4] text-[#1F1A17] border border-[#E7D2B8]'
                        : 'text-[#6E6258] hover:text-[#1F1A17] hover:bg-[#FFE4C4]/60 border border-transparent'
                    } ${collapsed ? 'justify-center' : ''}`
                  }
                >
                  <div className="relative shrink-0">
                    <Icon size={20} />
                    {collapsed && showBadge && (
                      <span className="absolute -top-1.5 -right-1.5 bg-amber-500 text-white text-[9px] font-bold rounded-full w-4 h-4 flex items-center justify-center">
                        {pendingCount > 9 ? '9+' : pendingCount}
                      </span>
                    )}
                  </div>
                  {!collapsed && (
                    <>
                      <span className="flex-1">{label}</span>
                      {showBadge && (
                        <span className="bg-amber-500 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center shrink-0">
                          {pendingCount > 9 ? '9+' : pendingCount}
                        </span>
                      )}
                    </>
                  )}
                </NavLink>
              );
            })}
        </nav>

        {/* User section */}
        <div className="border-t border-[#E7D2B8] p-3 shrink-0">
          {!collapsed && user && (
            <div className="mb-3 px-1">
              <p className="text-sm font-medium text-[#1F1A17] truncate">{user.name}</p>
              <p className="text-xs text-[#6E6258] truncate">{user.email}</p>
              <span className={`inline-block mt-1.5 text-xs px-2 py-0.5 rounded border capitalize ${roleColors[user.role] || roleColors.caster}`}>
                {user.role}
              </span>
            </div>
          )}
          <Button
            data-testid="logout-btn"
            variant="ghost"
            onClick={logout}
            className={`text-[#6E6258] hover:text-[#1F1A17] hover:bg-[#FFE4C4]/60 ${collapsed ? 'w-full justify-center px-0' : 'w-full justify-start'}`}
            size="sm"
          >
            <LogOut size={18} />
            {!collapsed && <span className="ml-2">Sign Out</span>}
          </Button>
        </div>
      </aside>

      {/* Main content */}
      <main className={`flex-1 transition-all duration-300 ${collapsed ? 'ml-16' : 'ml-64'}`}>
        <div className="p-8 max-w-7xl mx-auto">
          <Outlet />
        </div>
      </main>
    </div>
  );
}